import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { ShareSelectionContext, TransactContext } from '@arrived/contexts';
import { Rewards } from '@arrived/models';
import { useGetCurrentUserQuery, useGetPrimaryAccountQuery, useGetVouchersQuery } from '@arrived/queries';

interface VoucherContextValue {
  loaded: boolean;
  setLoaded: (loaded: boolean) => void;
  selectedVouchers: Rewards.Voucher[];
  setSelectedVouchers: (vouchers: Rewards.Voucher[]) => void;
  voucherErrors: string[];
  setVoucherErrors: (errors: string[]) => void;
}

interface VoucherHandlerProps {
  children?: ReactNode;
}

interface VoucherErrors {
  MAX_VOUCHERS: string;
  EXCEEDS_SUBTOTAL: string;
  INVALID_VOUCHER: string;
}

export const VoucherContext = createContext<VoucherContextValue>({
  loaded: false,
  setLoaded: () => null,
  selectedVouchers: [],
  setSelectedVouchers: () => null,
  voucherErrors: [],
  setVoucherErrors: () => null,
});

export const voucherErrorMessages: VoucherErrors = {
  MAX_VOUCHERS: 'You can only apply up to 10 vouchers at a time. Please remove some vouchers.',
  EXCEEDS_SUBTOTAL: 'The total amount of vouchers cannot exceed the subtotal of your trade',
  INVALID_VOUCHER: 'Cannot redeem used or revoked vouchers. Please remove vouchers.',
};

export const useVoucherContext = () => useContext(VoucherContext);

export const VoucherHandler = ({ children }: VoucherHandlerProps) => {
  const { subtotal } = useContext(ShareSelectionContext);
  const { setAppliedVouchers, setAvailableVouchers, setTransactVoucherErrors } = useContext(TransactContext);
  const [loaded, setLoaded] = useState(false);
  const [selectedVouchers, setSelectedVouchers] = useState<Rewards.Voucher[]>([]);
  const [voucherErrors, setVoucherErrors] = useState<string[]>([]);
  const user = useGetCurrentUserQuery();
  const account = useGetPrimaryAccountQuery();

  const vouchers = useGetVouchersQuery({ userId: user.data?.id, accountId: account.data?.id });
  const unusedVouchers: Rewards.Voucher[] =
    vouchers?.data?.vouchers?.filter((voucher) => voucher.state === 'CREATED') ?? [];

  const removeVoucherError = useCallback(
    (voucherErrorString: string) => {
      return setVoucherErrors(voucherErrors.filter((error) => error !== voucherErrorString));
    },
    [voucherErrors]
  );

  useEffect(() => {
    if (vouchers.data && !loaded) {
      setAvailableVouchers(unusedVouchers);
      setLoaded(true);
    }
  }, [vouchers]);

  useEffect(() => {
    const voucherErrorString = voucherErrorMessages.MAX_VOUCHERS;

    if (selectedVouchers.length <= 10) {
      setAvailableVouchers(unusedVouchers ?? []);
      setAppliedVouchers(selectedVouchers);
      removeVoucherError(voucherErrorString);
    } else {
      setVoucherErrors([...voucherErrors, voucherErrorString]);
    }
  }, [selectedVouchers]);

  useEffect(() => {
    const voucherErrorString = voucherErrorMessages.EXCEEDS_SUBTOTAL;
    const selectedVouchersTotal: number = selectedVouchers.reduce((acc, voucher) => acc + Number(voucher.amount), 0);
    if (selectedVouchersTotal > subtotal) {
      // If error is already in array, do not add it again
      if (!voucherErrors.includes(voucherErrorString)) {
        setVoucherErrors([...voucherErrors, voucherErrorString]);
      }
    } else {
      removeVoucherError(voucherErrorString);
    }
  }, [subtotal, selectedVouchers]);

  /*
     Check if any of the selected vouchers are invalid, if so, set voucher errors
     in TransactContext to Disable the submit button.
  */

  useEffect(() => {
    if (voucherErrors.length > 0) {
      setTransactVoucherErrors(voucherErrors);
    } else {
      setTransactVoucherErrors([]);
    }
  }, [voucherErrors]);

  const memoizedValues = useMemo(() => {
    return {
      loaded,
      setLoaded,
      selectedVouchers,
      setSelectedVouchers,
      voucherErrors,
      setVoucherErrors,
    };
  }, [loaded, selectedVouchers, voucherErrors]);

  return <VoucherContext.Provider value={memoizedValues}>{children}</VoucherContext.Provider>;
};
